import React, {useEffect, useState} from "react";
import {Alert, Box, Button, ExpandableSection, FileDropzone, FileInput, FileTokenGroup, Header, SpaceBetween} from "@amzn/awsui-components-react";
import {ACTIONS} from "src/services/calculation-builder/JESettingsReducer";
import ServiceCollection from "src/services/ServiceCollection";
import TPEAction from "src/models/common/TPEAction";
import {UserProfile} from "src/models/users/UserProfile";

interface UploadJournalEntryFileProps {
    services: ServiceCollection,
    dispatch: React.Dispatch<TPEAction>,
    recordNumber: string,
    userProfile: UserProfile
}

export default function UploadJournalEntryFile (props: UploadJournalEntryFileProps) {
    const { services, dispatch, recordNumber, userProfile } = props
    const [isExpanded, setIsExpanded] = useState(true);
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [s3KeyForUpload, setS3KeyForUpload] = useState<string>("");
    const [showUploadError, setShowUploadError] = useState(false);

    const [uploadJETemplateS3Key, uploadJETemplateLoading, uploadJETemplateError] = services.journalsService.uploadJETemplate(uploadFile as unknown as File, s3KeyForUpload, recordNumber);

    useEffect(() => {
        if (uploadJETemplateS3Key) {
            services.messageService.showSuccessAutoDismissBanner(`${uploadFile?.name} successfully uploaded.`, 10000);
            dispatch(ACTIONS.UPLOAD_JE_TEMPLATE_SUCCESS.withPayload(true));
        }
    }, [uploadJETemplateS3Key]);

    useEffect(() => {
        if (uploadJETemplateError) {
            setShowUploadError(true);
        }
    }, [uploadJETemplateError]);

    const sanitizeFileName = (fileName: string): string => {
        return fileName
            .replace(/[\\/:*?"<>|]/g, '_')           // Replace problematic characters
            .replace(/[\x00-\x1f\x7f-\x9f]/g, '')    // Remove control characters
            .replace(/^[.]+|[.]+$/g, '')             // Remove leading/trailing dots
            .replace(/\s+/g, '_')                    // Replace spaces with underscores
            .substring(0, 200);                      // Limit length to prevent overly long keys
    };
    
    const uploadJEFile = () => {
        if (!uploadFile) return;
        
        dispatch(ACTIONS.UPLOAD_JE_TEMPLATE_SUCCESS.withPayload(false));
        setShowUploadError(false); // Clear error alert when uploading

        const sanitizedFileName = sanitizeFileName(uploadFile.name);
        const key = `${new Date().getTime()}_${userProfile.user}_${sanitizedFileName}`;
        setS3KeyForUpload(key);
    }

    const handleFileSelect = (file: File | null) => {
        if (!file) return
        
        setShowUploadError(false); // Clear error alert when choosing file
        
        const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
        if (fileExtension !== '.xlsx') {
            services.messageService.showErrorBanner('Invalid file format. Please upload an .xlsx file.');
            return;
        }

        setUploadFile(file);
    };

    return (
        <ExpandableSection 
            className="polarisExpandableSection configureJESettingsSection" variant="container"
            defaultExpanded={isExpanded}
            onChange={({ detail }) => setIsExpanded(detail.expanded)}
            header={<Header variant="h2">JE Templates</Header>}>
            <SpaceBetween direction="vertical" size="s">
                <Header
                    variant="h3"
                    description="Journal entry template with seven segments and all neccessary information filled up."
                    actions={
                        <Button
                            disabled={uploadJETemplateLoading || !uploadFile}
                            loading={uploadJETemplateLoading}
                            onClick={() => uploadJEFile()}
                        >
                            Upload JE File
                        </Button>
                    }
                >
                    Journal Entry File
                </Header>
                <SpaceBetween size="l">
                    <FileDropzone
                        onChange={({ detail }) => handleFileSelect(detail.value[0] || null)}>
                        <SpaceBetween size="xxs" alignItems="center">
                            <Box color="inherit">
                                Drop and drop files here or select using the button below
                            </Box>
                            <FileInput
                                value={uploadFile ? [uploadFile] : []}
                                onChange={({ detail }) => handleFileSelect(detail.value[0] || null)}>
                                Choose files
                            </FileInput>
                        </SpaceBetween>
                    </FileDropzone>
                    <FileTokenGroup
                        items={uploadFile ? [{ file: uploadFile }] : []}
                        onDismiss={() => setUploadFile(null)}
                        i18nStrings={{
                            limitShowFewer: "Show fewer files",
                            limitShowMore: "Show more files",
                            errorIconAriaLabel: "Error",
                            warningIconAriaLabel: "Warning"
                        }}
                    />
                    {uploadJETemplateError && (
                        <Alert type="error" visible={showUploadError}>
                            {uploadJETemplateError}
                        </Alert>
                    )}
                </SpaceBetween>
            </SpaceBetween>
        </ExpandableSection>
    );
}